import { useEffect, useState } from 'react';
import { Modal } from '../../components/modal/modal';
import { Spinner } from '../../components/spinner/spinner';
import { Field } from '../../components/field/field';
import { toast } from '../../util/toasts';
import { UserEquipment } from '../../clients/quipli-service/user/user';
import { useUserAPI } from '../../clients/quipli-service/user/user-api';
import { Typography } from '../../components/typography/typography';

export const UserEquipmentForm = ({
  userId,
  isOpen,
  userEquipment,
  close,
  setNewUserEquipment,
}: {
  userId: number;
  isOpen: boolean;
  userEquipment?: UserEquipment;
  close: () => any;
  setNewUserEquipment?: (u: UserEquipment) => void;
}) => {
  const userAPI = useUserAPI();

  const [description, setDescription] = useState<string>('');
  const [make, setMake] = useState<string | undefined>('');
  const [model, setModel] = useState<string | undefined>('');
  const [serialNumber, setSerialNumber] = useState<string | undefined>('');

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const clear = () => {
    setDescription('');
    setMake('');
    setModel('');
    setSerialNumber('');

    close();
  };

  const onSubmit = async () => {
    if (isDisabled) return;

    setIsLoading(true);
    try {
      const newUserEquipment = await (userEquipment
        ? userAPI.updateUserEquipment(userEquipment.id, {
            ...userEquipment,
            description: description.trim(),
            make: make?.trim(),
            model: model?.trim(),
            serialNumber: serialNumber?.trim(),
          })
        : userAPI.createUserEquipment({
            userId,
            description: description.trim(),
            make: make?.trim(),
            model: model?.trim(),
            serialNumber: serialNumber?.trim(),
          }));
      setNewUserEquipment && setNewUserEquipment(newUserEquipment);
      setIsLoading(false);
      onClose();
    } catch (e: any) {
      setIsLoading(false);
      if (e.message.msg) {
        toast(e.message.msg, 'error');
      } else if (e.message) {
        toast(e.message, 'error');
      }
    }
  };

  const onClose = () => {
    userEquipment ? close() : clear();
  };

  useEffect(() => {
    if (userEquipment) {
      setDescription(userEquipment.description);
      setMake(userEquipment.make);
      setModel(userEquipment.model);
      setSerialNumber(userEquipment.serialNumber);
    }
  }, [userEquipment]);

  useEffect(() => {
    setIsDisabled(description?.length === 0 && (make?.length === 0 || model?.length === 0));
  }, [description, make, model, serialNumber]);

  return (
    <Modal
      isOpen={isOpen}
      title={`${userEquipment ? 'Edit' : 'Add new'} equipment`}
      actions={
        isLoading ? (
          <Spinner />
        ) : (
          <>
            <button className="button button-block button-secondary" data-cy="cancel_user" onClick={onClose}>
              Cancel
            </button>

            <button
              className="button button-yellow button-primary"
              data-cy="confirm_user"
              onClick={onSubmit}
              disabled={isDisabled}
            >
              {userEquipment ? 'Save' : 'Add'}
            </button>
          </>
        )
      }
      close={onClose}
    >
      <Field label={'Description'} dataCy="description" type="text" value={description} onChange={setDescription} />

      <div className="text-center px-4">
        <Typography type="text-md" className="text-grey-500">
          OR
        </Typography>
      </div>

      <Field label="Make" dataCy="make" type="text" value={make} onChange={setMake} />

      <Field label="Model" dataCy="model" type="text" value={model} onChange={setModel} />

      <Field label="Serial Number" dataCy="serialNumber" type="text" value={serialNumber} onChange={setSerialNumber} />
    </Modal>
  );
};
